import { uuid } from "uuidv4";
import { fileStore } from "./fileStore";
import { templateActions } from "@pages/Applications/C&C/C&CSetup/templateStore";

export function remapKeys(
    obj: Record<string, any>,
    keyMappings: Record<string, string>
) {
    const temp: Record<string, any> = {};
    for (const [oldKey, newKey] of Object.entries(keyMappings)) {
        temp[newKey] = obj[oldKey];
    }
    for (const oldKey of Object.keys(keyMappings)) {
        delete obj[oldKey];
    }
    Object.assign(obj, temp);
    return obj;
}
export const handleDragEnd = (
    result: any,
    path: any,
    data: any,
    section: string,
    deviceType: string
) => {
    if (section !== "Section1") {
        if (!result.destination) return;
        if (result.source.index === result.destination.index) return;
        const items = [...data];
        const [movedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, movedItem);
        const updatedItems = items.map((item, index) => ({
            ...item,
            Rank: index + 1,
            id: item.id || uuid(),
        }));
        const keyMappings: Record<string, string> = {};
        const pathPrefix = path.join(".");
        updatedItems.forEach((item) => {
            if (item.Pub?.Url?.startsWith("blob:")) {
                const oldKey = Object.keys(fileStore).find((key) => {
                    return (
                        key.startsWith(pathPrefix) &&
                        fileStore[key] &&
                        item.Pub.Name === fileStore[key]?.name
                    );
                });
                const newKey = `${pathPrefix}.${item.Rank}`;
                if (oldKey) {
                    keyMappings[oldKey] = newKey;
                } else {
                    console.warn(
                        "Blob file not found in store for item:",
                        item
                    );
                }
            }
        });
        remapKeys(fileStore, keyMappings);
        const itemsWithoutId = updatedItems.map(({ id, ...rest }) => rest);
        try {
            templateActions.updateTemplate(path, itemsWithoutId);
        } catch (error) {
            console.error("Update failed:", error);
        }
    } else {
        if (!result.destination) return;
        if (result.destination.index === result.source.index) return;
        const sectionData = templateActions.getNestedObject(path);
        const imageKeys = Object.keys(sectionData)
            .filter((key) => key.startsWith("Image"))
            .sort(
                (a, b) =>
                    parseInt(a.replace("Image", "")) -
                    parseInt(b.replace("Image", ""))
            );
        const [removedKey] = imageKeys.splice(result.source.index, 1);
        imageKeys.splice(result.destination.index, 0, removedKey);
        const keyMappings: Record<string, string> = {};
        const pathPrefix = path.join(".");
        imageKeys.forEach((oldKey, newIndex) => {
            const newKey = `Image${newIndex + 1}`;
            const mediaData = sectionData[oldKey]?.[deviceType];
            if (mediaData?.Url?.startsWith("blob:")) {
                const fileStoreKey = Object.keys(fileStore).find(
                    (key) =>
                        key.startsWith(
                            `${pathPrefix}.${oldKey}.${deviceType}`
                        ) && fileStore[key]?.name === mediaData.Name
                );
                if (fileStoreKey) {
                    const newFileStoreKey = `${pathPrefix}.${newKey}.${deviceType}`;
                    keyMappings[fileStoreKey] = newFileStoreKey;
                }
            }
        });
        if (Object.keys(keyMappings).length > 0) {
            remapKeys(fileStore, keyMappings);
        }
        const newData: Record<string, any> = {};
        imageKeys.forEach((oldKey, newIndex) => {
            const newKey = `Image${newIndex + 1}`;
            newData[newKey] = sectionData[oldKey];
        });
        Object.keys(sectionData)
            .filter((key) => !key.startsWith("Image"))
            .forEach((key) => {
                newData[key] = sectionData[key];
            });
        try {
            templateActions.updateTemplate(path, newData);
        } catch (error) {
            console.error("Update failed:", error);
        }
    }
};

const MAGIC_BYTES: Record<string, number[][]> = {
    "image/png": [[0x89, 0x50, 0x4e, 0x47]],
    "image/jpeg": [[0xff, 0xd8, 0xff]],
    "image/jpg": [[0xff, 0xd8, 0xff]],
    "image/webp": [[0x52, 0x49, 0x46, 0x46]],
    "video/mp4": [[0x00, 0x00, 0x00]],
    "video/webm": [[0x1a, 0x45, 0xdf, 0xa3]],
};

export const validateFileContent = async (file: File): Promise<boolean> => {
    const signatures = MAGIC_BYTES[file.type];
    if (!signatures) return false;

    const buffer = await file.slice(0, 12).arrayBuffer();
    const bytes = new Uint8Array(buffer);

    return signatures.some((sig) =>
        sig.every((byte, i) => bytes[i] === byte)
    );
};

export const checkDimensions = async (
    file: File | undefined,
    requiredWidth: number,
    requiredHeight: number
): Promise<boolean> => {
    if (!file) return false;

    const url = URL.createObjectURL(file);

    try {
        if (file.type.startsWith("image/")) {
            const img = new Image();

            const isValid = await new Promise<boolean>((resolve) => {
                img.onload = () => {
                    resolve(
                        img.naturalWidth === requiredWidth &&
                            img.naturalHeight === requiredHeight
                    );
                };

                img.onerror = () => resolve(false);
                img.src = url;
            });

            img.src = "";
            return isValid;
        }

        if (file.type.startsWith("video/")) {
            const video = document.createElement("video");
            video.preload = "metadata";

            const isValid = await new Promise<boolean>((resolve) => {
                video.onloadedmetadata = () => {
                    resolve(
                        video.videoWidth === requiredWidth &&
                            video.videoHeight === requiredHeight
                    );
                };

                video.onerror = () => resolve(false);
                video.src = url;
            });

            video.src = "";
            video.load();
            return isValid;
        }

        return false;
    } finally {
        URL.revokeObjectURL(url);
    }
};
export const extractDimensions = (
    input: string
): { width: number; height: number } | null => {
    const match = input.match(/(\d+)\s*\*\s*(\d+)/);

    if (!match) return null;

    return {
        width: Number(match[1]),
        height: Number(match[2]),
    };
};
